import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseModal } from "@/redux/actions/modal";
import {
  LENDER_HUB_OFFER_REDUCER_PATH,
  LENDER_HUB_OFFER_RELATED_TAGS,
  LENDER_HUB_OFFER_SERVICE_TAG,
  LENDER_HUB_OFFER_FILE_SERVICE_TAG,
} from "@/features/LenderHub/Offer/utils/constants/lenderHubOfferServiceTags";
import { getQueryParams } from "@/utils/httpRequest";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { lenderHubDealService } from "@/features/LenderHub/Deal/store/services/lenderHubDealService";
import { LENDER_HUB_DEAL_SERVICE_TAG } from "@/features/LenderHub/Deal/utils/constants/lenderHubDealServiceTags";

export const lenderHubOfferService = createApi({
  reducerPath: LENDER_HUB_OFFER_REDUCER_PATH,
  tagTypes: LENDER_HUB_OFFER_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
  }),
  endpoints: builder => ({
    // Offer Endpoints
    createOffer: builder.mutation({
      query: body => ({
        url: `deals/${body.dealId}/offers`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_CREATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been created successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(
            lenderHubDealService.util.invalidateTags([{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: data?.lenderBoardId }]),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    getOffers: builder.query({
      query: ({ dealId, ...params }) => `deals/${dealId}/offers?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.map(({ lenderBoardOfferId }) => ({
                type: LENDER_HUB_OFFER_SERVICE_TAG,
                id: lenderBoardOfferId,
              })),
              { type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),

    getOffer: builder.query({
      query: ({ dealId, lenderBoardOfferId }) => `deals/${dealId}/offers/${lenderBoardOfferId}`,
      providesTags: (result, error, { lenderBoardOfferId }) => [
        { type: LENDER_HUB_OFFER_SERVICE_TAG, id: lenderBoardOfferId },
      ],
    }),

    updateOffer: builder.mutation({
      query: ({ dealId, lenderBoardOfferId, ...body }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_OFFER_SERVICE_TAG, id: arg.lenderBoardOfferId },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_UPDATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been updated successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    deleteOffer: builder.mutation({
      query: ({ dealId, lenderBoardOfferId }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted({ dealId }, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_DELETED);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderHubDealService.util.invalidateTags([{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: dealId }]));
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    // Offer Files Endpoints
    createOfferFile: builder.mutation({
      query: ({ dealId, lenderBoardOfferId, uploadIds }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}/files`,
        method: "POST",
        body: { uploadIds },
      }),
      invalidatesTags: () => [
        { type: LENDER_HUB_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_FILE_UPLOADED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description:
                  data.length === 1 ? "File has been uploaded successfully." : "Files have been uploaded successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    getOfferFiles: builder.query({
      query: ({ dealId, lenderBoardOfferId, ...params }) =>
        `deals/${dealId}/offers/${lenderBoardOfferId}/files?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.map(({ lenderBoardOfferFileId }) => ({
                type: LENDER_HUB_OFFER_FILE_SERVICE_TAG,
                id: lenderBoardOfferFileId,
              })),
              { type: LENDER_HUB_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_HUB_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),

    getOfferFile: builder.query({
      query: ({ dealId, lenderBoardOfferId, lenderBoardOfferFileId }) =>
        `deals/${dealId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}`,
      providesTags: (result, error, { lenderBoardOfferFileId }) => [
        { type: LENDER_HUB_OFFER_FILE_SERVICE_TAG, id: lenderBoardOfferFileId },
      ],
    }),

    previewOfferFile: builder.mutation({
      query: ({ dealId, lenderBoardOfferId, lenderBoardOfferFileId }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}/preview`,
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_FILE_PREVIEWED, data.file);

          // Open preview in new tab using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File download started successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    downloadOfferFile: builder.mutation({
      query: ({ dealId, lenderBoardOfferId, lenderBoardOfferFileId }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}/download`,
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_FILE_DOWNLOADED, data.file);

          // Trigger download using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.download = data.file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File download started successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    deleteOfferFile: builder.mutation({
      query: ({ dealId, lenderBoardOfferId, lenderBoardOfferFileId }) => ({
        url: `deals/${dealId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: LENDER_HUB_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const data = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_OFFER_FILE_DELETED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File has been deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  // Offer hooks
  useCreateOfferMutation,
  useGetOffersQuery,
  useGetOfferQuery,
  useUpdateOfferMutation,
  useDeleteOfferMutation,
  // Offer Files hooks
  useCreateOfferFileMutation,
  useGetOfferFilesQuery,
  usePreviewOfferFileMutation,
  useDownloadOfferFileMutation,
  useDeleteOfferFileMutation,
} = lenderHubOfferService;
